import {
  Button,
  Card,
  Checkbox,
  Dropdown,
  Empty,
  Modal,
  Tooltip,
  notification,
  Select,
  Tabs,
  Typography
} from 'antd';
import TransactionTable from 'components/cashier/transaction-table';
import React, { useEffect, useState } from 'react';
import {
  BankOutlined,
  CaretDownFilled,
  HistoryOutlined,
  InfoCircleOutlined,
  MoreOutlined
} from '@ant-design/icons';
import { TRANSACTION_PROCESSING_ACTION } from 'constants/cashier';
import useModal from 'stores/useModal';
import useModalStore from 'stores/useModal';
import useBookingStore from 'stores/useBooking';
import { upperCase } from 'lodash';
import useCashierStore from 'stores/useCashier';
import { CustomMenuItem, PermissionActionTypes } from 'helper/type/action.type';
import { usePermissions } from 'AuthorizePermission';

import { useFilterActions } from 'helper/common/permission.action.helper';
import IBButton from 'helper/permission.action';
import { useTranslation } from 'react-i18next';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import { adjustOrderInvoice, updateOrderDebitPartner } from 'services/api/module/booking.api';
import { useMutation } from '@tanstack/react-query';
import { useGetBookingDetail } from 'hooks/useGetBookingDetail';
import { useGetTravelAgencies } from 'hooks/useGetTravelAgencies';
import { useGetCustomerById } from 'hooks/useGetCustomerById';
import { useGetGuestOrderDetail } from 'hooks/useGetGuestTransactions';

const getDebitPartnerDisplayName = (
  orderDetail?: {
    debit_partner_name?: string | null;
    guest_name?: string | null;
    main_guest_id?: number | null;
    debit_partner_id?: number | null;
    debit_subject_type?: 'travel_agency' | 'partner' | null;
  },
  mainGuestSuffix: string = 'Main Guest'
) => {
  const baseName = orderDetail?.debit_partner_name || orderDetail?.guest_name || '—';
  const isMainGuest = getIsMainGuest(orderDetail);
  return isMainGuest ? `${baseName} (${mainGuestSuffix})` : baseName;
};

const getIsMainGuest = (orderDetail?: {
  main_guest_id?: number | null;
  debit_partner_id?: number | null;
}) => {
  return (
    orderDetail?.main_guest_id != null &&
    orderDetail?.debit_partner_id != null &&
    orderDetail.main_guest_id === orderDetail.debit_partner_id
  );
};

const getDebitSubjectLabel = (
  subjectType: 'travel_agency' | 'partner' | null | undefined,
  labels: { travelAgency: string; partner: string }
) => {
  if (subjectType === 'travel_agency') return labels.travelAgency;
  if (subjectType === 'partner') return labels.partner;
  return '';
};

interface CashierTransactionProps {
  orders: any[];
  booking_line_id?: number;
  guest_id?: number;
}

export const CashierTransaction = (props: CashierTransactionProps) => {
  const { t } = useTranslation();
  const { orders, guest_id, booking_line_id } = props;
  const {
    openPostRoomCharge,
    openMoveTransaction,
    openPostPaymentTransaction,
    openCancelTransactions,
    openPostRefundTransaction,
    openCreateInvoiceTransaction,
    setIsOpenTransactionHistory,
    openMoveSaleOrder,
    openUndoCancelledTransactions,
    openSplitTransactions,
    setInfoConfirmModal,
    setConfirmLoading
  } = useModal();
  const { setIsOpenAddService } = useModalStore();
  const { setBookingLineId } = useBookingStore();
  const { setFolioBalanceCode } = useCashierStore();
  const [hideDeleted, setHideDeleted] = useState(true);
  const [cancelledCount, setCancelledCount] = useState(0);

  const [transactionIds, setTransactionIds] = useState<number[] | []>([]);
  const [cancelledTransactionIds, setCancelledTransactionIds] = useState<number[] | []>([]);
  const [isSplitTransactions, setIsSplitTransactions] = useState<boolean>(false);
  const [code, setCode] = useState<string>('A');

  const { data: bookingLineDetail } = useGetBookingDetail(booking_line_id);

  const [activeDebitOrderId, setActiveDebitOrderId] = useState<number | undefined>(undefined);

  const [selectedDebitProfileId, setSelectedDebitProfileId] = useState<number | undefined>(
    undefined
  );
  const [taSearchInput, setTaSearchInput] = useState('');
  const [taSearch, setTaSearch] = useState('');
  useEffect(() => {
    const tmr = setTimeout(() => setTaSearch(taSearchInput), 500);
    return () => clearTimeout(tmr);
  }, [taSearchInput]);
  const [selectedTravelAgencyId, setSelectedTravelAgencyId] = useState<number | undefined>(
    undefined
  );
  const { data: travelAgencies } = useGetTravelAgencies({ search: taSearch });

  const selectedAgency = (travelAgencies || []).find(a => a.id === selectedTravelAgencyId);
  const { data: debitPartnerInfo } = useGetCustomerById(
    Number(selectedAgency?.debit_partner_id || 0)
  );
  const { data: activeOrderDetail } = useGetGuestOrderDetail(activeDebitOrderId);

  type DebitPartnerWizardStep = 'select' | 'booking_guest' | 'ta_company';
  const [isOpenDebitPartnerWizard, setIsOpenDebitPartnerWizard] = useState(false);
  const [debitWizardStep, setDebitWizardStep] = useState<DebitPartnerWizardStep>('select');

  const currentDebitPartnerLabel = getDebitPartnerDisplayName(
    activeOrderDetail,
    t('cashierPage.debitPartner.mainGuestTag')
  );

  const { permissions, hasPermission } = usePermissions();

  const { mutateAsync: mutateAdjustOrder } = useMutation({
    mutationFn: (guest_order_id: number) => adjustOrderInvoice(guest_order_id)
  });

  const { mutateAsync: mutateUpdateDebitPartner, isPending: isUpdatingDebitPartner } = useMutation({
    mutationFn: (params: {
      guest_id: number;
      guest_order_id: number;
      debit_partner_id?: number;
      debit_travel_agency_id?: number;
    }) => updateOrderDebitPartner(params)
  });

  const items: CustomMenuItem[] = [
    {
      key: TRANSACTION_PROCESSING_ACTION.VOID_TRANSACTION,
      label: t('cashierPage.transactionProcesses.voidTransaction'),
      permission: PermissionActionTypes.VOID_TRANSACTION,
      disabled: transactionIds.length === 0 || isSplitTransactions
    },
    {
      key: TRANSACTION_PROCESSING_ACTION.MOVE_TRANSACTION,
      label: t('cashierPage.transactionProcesses.moveTransaction'),
      permission: PermissionActionTypes.MOVE_TRANSACTION,
      disabled: transactionIds.length === 0
    },
    {
      key: TRANSACTION_PROCESSING_ACTION.UNDO_VOIDED,
      label: t('cashierPage.transactionProcesses.undoCancelledTransaction'),
      permission: PermissionActionTypes.UNDO_VOIDED_TRANSACTION,
      disabled: cancelledTransactionIds.length === 0
    },
    {
      key: TRANSACTION_PROCESSING_ACTION.SPLIT_TRANSACTIONS,
      label: t('cashierPage.transactionProcesses.splitTransactions'),
      permission: PermissionActionTypes.SPLIT_TRANSACTIONS,
      disabled: transactionIds.length === 0 || transactionIds.length > 1
    }
  ];

  const menuPermission = useFilterActions(items, permissions, hasPermission);

  const clearDebitWizardForm = () => {
    setSelectedDebitProfileId(undefined);
    setSelectedTravelAgencyId(undefined);
    setTaSearchInput('');
  };

  const handleOpenDebitPartnerWizard = (orderId: number) => {
    setActiveDebitOrderId(orderId);
    setDebitWizardStep('select');
    clearDebitWizardForm();
    setIsOpenDebitPartnerWizard(true);
  };

  const handleCloseDebitPartnerWizard = () => {
    setIsOpenDebitPartnerWizard(false);
    setDebitWizardStep('select');
    setActiveDebitOrderId(undefined);
    clearDebitWizardForm();
  };

  const handleConfirmDebitByBookingGuest = async () => {
    if (!guest_id || !activeDebitOrderId || !selectedDebitProfileId) return;
    try {
      await mutateUpdateDebitPartner({
        guest_id,
        guest_order_id: activeDebitOrderId,
        debit_partner_id: selectedDebitProfileId
      });
      notification.success({ message: t('common.message.saveSuccessfully') });
      handleCloseDebitPartnerWizard();
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, guest_id]
      });
      await queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.GET_GUEST_TRANSACTIONS,
          QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL,
          activeDebitOrderId
        ]
      });
    } catch (err: any) {
      notification.error({ message: err?.error || err?.message || t('common.error') });
    }
  };

  const handleConfirmDebitByTA = async () => {
    if (!guest_id || !activeDebitOrderId || !selectedTravelAgencyId) return;
    try {
      await mutateUpdateDebitPartner({
        guest_id,
        guest_order_id: activeDebitOrderId,
        debit_travel_agency_id: selectedTravelAgencyId
      });
      notification.success({ message: t('common.message.saveSuccessfully') });
      handleCloseDebitPartnerWizard();
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, guest_id]
      });
      await queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.GET_GUEST_TRANSACTIONS,
          QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL,
          activeDebitOrderId
        ]
      });
    } catch (err: any) {
      notification.error({ message: err?.error || err?.message || t('common.error') });
    }
  };

  // Initial code
  useEffect(() => {
    setCode(orders?.at(0)?.folio_balance_code || 'A');
  }, [orders, guest_id]);

  // with memo
  const currentOrder = React.useMemo(() => {
    return orders?.filter(item => item.folio_balance_code === code)?.at(0);
  }, [code, orders]);

  useEffect(() => {
    setHideDeleted(true);
  }, [currentOrder]);

  const handleShowAddService = () => {
    if (booking_line_id) {
      setBookingLineId(booking_line_id);
      setIsOpenAddService(true);
      setFolioBalanceCode(code);
    }
  };

  const postRoomCharge = () => {
    if (booking_line_id) {
      openPostRoomCharge(booking_line_id);
    }
  };

  const postPayment = () => {
    if (guest_id && code && booking_line_id && orders) {
      openPostPaymentTransaction(code, booking_line_id, guest_id, currentOrder?.id);
    }
  };

  const postRefund = () => {
    if (guest_id && code && booking_line_id && orders) {
      openPostRefundTransaction(code, booking_line_id, guest_id, currentOrder?.id);
    }
  };

  const handleCreateInvoice = () => {
    if (guest_id && code && booking_line_id) {
      openCreateInvoiceTransaction(guest_id, code, booking_line_id, true, currentOrder?.id);
    }
  };

  const handleCreateCorrectionOrder = () => {
    setInfoConfirmModal(true, {
      title: t('cashierPage.adjustOrderInvoice.title'),
      onOk: async () => {
        setInfoConfirmModal(false);
        setConfirmLoading(true);
        try {
          await mutateAdjustOrder(currentOrder?.id);
          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS]
          });
          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST]
          });
          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
          });
          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_TRANSACTION_HISTORY_LOGS]
          });
          notification.success({
            message: t('cashierPage.adjustOrderInvoice.success')
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.KEY_ROOM]
          });
        } catch (err: any) {
          notification.error({
            message: err.error || t('common.error')
          });
        } finally {
          setConfirmLoading(false);
        }
      }
    });
  };

  const handleShowHistory = () => {
    setIsOpenTransactionHistory(true, currentOrder?.id, booking_line_id);
  };

  const handleMoveSaleOrder = () => {
    openMoveSaleOrder(Number(booking_line_id), Number(guest_id), Number(currentOrder.id));
  };

  const handleMenuClick = (menu: any) => {
    switch (menu.key) {
      case TRANSACTION_PROCESSING_ACTION.MOVE_TRANSACTION:
        if (guest_id && booking_line_id) {
          openMoveTransaction(transactionIds, booking_line_id, code, guest_id);
        }
        break;
      case TRANSACTION_PROCESSING_ACTION.VOID_TRANSACTION:
        openCancelTransactions(transactionIds);
        break;
      case TRANSACTION_PROCESSING_ACTION.UNDO_VOIDED:
        openUndoCancelledTransactions(cancelledTransactionIds);
        break;
      case TRANSACTION_PROCESSING_ACTION.SPLIT_TRANSACTIONS:
        openSplitTransactions(transactionIds[0]);
        break;
    }
  };

  const updateCancelledCount = (count: number) => {
    setCancelledCount(count);
  };

  return (
    <Card className="pms-cashier__transaction">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <p className="title">
          {t('cashierPage.transactionList')} #{guest_id}
        </p>
        <Checkbox checked={hideDeleted} onChange={e => setHideDeleted(e.target.checked)}>
          {t('cashierPage.hideCancelledTxs')} ({cancelledCount})
        </Checkbox>
      </div>
      {orders ? (
        <>
          <Tabs
            onChange={(code: string) => setCode(code)}
            items={orders.map((item: any) => ({
              key: item.folio_balance_code,
              label: (
                <FolioTabLabel
                  orderId={item.id}
                  label={`${item.folio_balance_code} - ${item.sale_order_name} ${item.state === 'invoiced' ? `(${upperCase(item.state)})` : ''}`}
                  onOpenChangeDebitPartner={() => handleOpenDebitPartnerWizard(item.id)}
                />
              ),
              children: (
                <TransactionTable
                  orderId={item.id}
                  setTransactionIds={setTransactionIds}
                  setCancelledTransactionIds={setCancelledTransactionIds}
                  isInvoiced={item.state == 'invoiced'}
                  hideDeleted={hideDeleted}
                  onCancelledCountChange={updateCancelledCount}
                  setIsSplitTransactions={setIsSplitTransactions}
                  t={t}
                />
              )
            }))}
          ></Tabs>
        </>
      ) : (
        <Empty />
      )}

      <Modal
        centered
        width={560}
        open={isOpenDebitPartnerWizard}
        onCancel={handleCloseDebitPartnerWizard}
        destroyOnClose
        title={
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontWeight: 600 }}>{t('cashierPage.debitPartner.title')}</div>
            <div style={{ fontSize: 12, fontWeight: 400, opacity: 0.75 }}>
              {t('cashierPage.debitPartner.current')}: {currentDebitPartnerLabel}
            </div>
          </div>
        }
        footer={[
          debitWizardStep !== 'select' ? (
            <Button
              key="back"
              onClick={() => {
                setDebitWizardStep('select');
                clearDebitWizardForm();
              }}
            >
              {t('common.actions.back') || 'Back'}
            </Button>
          ) : null,
          <Button
            key="save"
            type="primary"
            className="ant-btn-secondary"
            loading={isUpdatingDebitPartner}
            disabled={
              debitWizardStep === 'select' ||
              (debitWizardStep === 'booking_guest' && !selectedDebitProfileId) ||
              (debitWizardStep === 'ta_company' && !selectedTravelAgencyId)
            }
            onClick={() => {
              if (debitWizardStep === 'booking_guest') handleConfirmDebitByBookingGuest();
              if (debitWizardStep === 'ta_company') handleConfirmDebitByTA();
            }}
          >
            {t('common.actions.save') || 'Save'}
          </Button>
        ]}
      >
        {debitWizardStep === 'select' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <Tooltip title={t('cashierPage.debitPartner.tooltipBookingGuest')}>
                <Button
                  className="ant-btn-secondary"
                  style={{ flex: 1 }}
                  onClick={() => setDebitWizardStep('booking_guest')}
                >
                  {t('cashierPage.debitPartner.bookingGuest')}
                </Button>
              </Tooltip>
              <Tooltip title={t('cashierPage.debitPartner.tooltipTaCompany')}>
                <Button
                  className="ant-btn-secondary"
                  style={{ flex: 1 }}
                  onClick={() => setDebitWizardStep('ta_company')}
                >
                  {t('cashierPage.debitPartner.taCompany')}
                </Button>
              </Tooltip>
            </div>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {t('cashierPage.debitPartner.description')}
            </Typography.Paragraph>
          </div>
        ) : debitWizardStep === 'booking_guest' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Select
              className="w-full"
              placeholder={t('cashierPage.debitPartner.selectGuest')}
              value={selectedDebitProfileId}
              onChange={value => setSelectedDebitProfileId(value)}
              options={(bookingLineDetail?.booking_line_guests || []).map((g: any) => ({
                label: g.guest_name,
                value: g.profile_id
              }))}
            />
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {t('cashierPage.debitPartner.bookingGuestHint')}
            </Typography.Paragraph>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Select
              showSearch
              allowClear
              className="w-full"
              placeholder={t('cashierPage.debitPartner.selectCompany')}
              value={selectedTravelAgencyId}
              onSearch={setTaSearchInput}
              filterOption={false}
              onChange={value => setSelectedTravelAgencyId(value)}
              options={(travelAgencies || [])
                .filter((a: any) => a.is_debit_partner === true)
                .map((a: any) => ({ label: a.name, value: a.id }))}
            />
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {t('cashierPage.debitPartner.debitPartnerLabel')}: {debitPartnerInfo?.name || '—'}
            </Typography.Paragraph>
          </div>
        )}
      </Modal>

      <div className="text-right" style={{ marginTop: 12 }}>
        <Button.Group className="flex-wrap">
          <Button className="ant-btn-secondary" onClick={handleShowHistory}>
            <HistoryOutlined />
            {t('cashierPage.transactionActions.history')}
          </Button>
          <IBButton
            permission={PermissionActionTypes.POST_ROOM_CHARGE}
            className="ant-btn-secondary"
            onClick={() => postRoomCharge()}
            disabled={!guest_id}
          >
            + {t('cashierPage.transactionActions.roomCharge')}
          </IBButton>
          <IBButton
            permission={PermissionActionTypes.HK_SERVICE}
            className="ant-btn-secondary"
            onClick={() => handleShowAddService()}
            disabled={!guest_id}
          >
            + {t('cashierPage.transactionActions.hkService')}
          </IBButton>
          <IBButton
            permission={PermissionActionTypes.MOVE_SALE_ORDER}
            className="ant-btn-secondary"
            onClick={() => handleMoveSaleOrder()}
            disabled={!currentOrder || !guest_id || currentOrder?.state === 'invoiced'}
          >
            {t('cashierPage.transactionActions.moveSO')}
          </IBButton>

          <Dropdown
            menu={{ items: menuPermission, onClick: handleMenuClick }}
            placement="top"
            trigger={['click']}
            disabled={
              (transactionIds.length === 0 && cancelledTransactionIds.length === 0) ||
              currentOrder?.state === 'invoiced'
            }
          >
            <Button className="ant-btn-secondary">
              {t('cashierPage.transactionActions.transactionProcessing')} <CaretDownFilled />
            </Button>
          </Dropdown>
          <IBButton
            permission={PermissionActionTypes.PAYMENT}
            className="ant-btn-secondary"
            onClick={() => postPayment()}
            disabled={!guest_id}
          >
            {t('cashierPage.transactionActions.payment')}
          </IBButton>
          <IBButton
            permission={PermissionActionTypes.REFUND}
            className="ant-btn-secondary"
            onClick={() => postRefund()}
            disabled={!guest_id || currentOrder?.state === 'invoiced'}
          >
            {t('cashierPage.transactionActions.refund')}
          </IBButton>
          <IBButton
            permission={PermissionActionTypes.INVOICE}
            className="ant-btn-secondary"
            onClick={() => handleCreateInvoice()}
            disabled={!currentOrder?.sale_order_name || currentOrder?.is_adjustment_order}
          >
            {t('cashierPage.transactionActions.invoice')}
          </IBButton>
          <IBButton
            permission={PermissionActionTypes.CORRECTION_INVOICE}
            className="ant-btn-secondary"
            onClick={() => handleCreateCorrectionOrder()}
            disabled={currentOrder?.state !== 'invoiced' || !currentOrder?.einvoice_signed}
          >
            {t('cashierPage.transactionActions.correctionInvoice')}
          </IBButton>
        </Button.Group>
      </div>
    </Card>
  );
};

const FolioTabLabel = ({
  orderId,
  label,
  onOpenChangeDebitPartner
}: {
  orderId: number;
  label: string;
  onOpenChangeDebitPartner: () => void;
}) => {
  const { t } = useTranslation();
  const { data: orderDetail } = useGetGuestOrderDetail(orderId);
  const debitPartnerDisplayName = getDebitPartnerDisplayName(
    orderDetail,
    t('cashierPage.debitPartner.mainGuestTag')
  );
  const isMainGuest = getIsMainGuest(orderDetail);
  const debitSubjectLabel = getDebitSubjectLabel(orderDetail?.debit_subject_type, {
    travelAgency: t('cashierPage.debitPartner.subjectType.travelAgency'),
    partner: t('cashierPage.debitPartner.subjectType.partner')
  });

  const infoIconColor =
    orderDetail?.debit_subject_type === 'travel_agency'
      ? '#1677ff'
      : orderDetail?.debit_subject_type === 'partner'
        ? '#595959'
        : '#8c8c8c';

  const debitInfoIcon =
    orderDetail?.debit_subject_type === 'travel_agency' ? (
      <BankOutlined style={{ color: infoIconColor }} />
    ) : (
      <InfoCircleOutlined style={{ color: infoIconColor }} />
    );

  const isLocked = !!orderDetail?.transactions?.some(
    (tx: any) =>
      (tx.transaction_group === 'payment' || tx.transaction_group === 'refund') &&
      tx.is_deleted === false
  );

  const isPosOrder = orderDetail?.order_type === 'pos';

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span>{label}</span>
      <Tooltip
        title={[debitPartnerDisplayName, isMainGuest ? '' : debitSubjectLabel]
          .filter(Boolean)
          .join(' • ')}
      >
        <Button
          size="small"
          type="text"
          aria-label="debit-partner-info"
          onClick={e => e.stopPropagation()}
          style={{ padding: 0, minWidth: 22, height: 22 }}
          icon={debitInfoIcon}
        />
      </Tooltip>
      {!isLocked && (
        <Tooltip title={t('cashierPage.debitPartner.tooltipOpenChange')}>
          <Button
            size="small"
            type="text"
            aria-label="folio-actions"
            onClick={e => {
              e.stopPropagation();
              onOpenChangeDebitPartner();
            }}
            style={{ padding: 0, minWidth: 22, height: 22 }}
            disabled={isPosOrder}
            icon={<MoreOutlined />}
          />
        </Tooltip>
      )}
    </div>
  );
};
